import { BILLING_INTERVALS } from '@oefy/core';
import { z } from 'zod';
import { pageFields, pageQuery, timestampSchema } from './common';
import { STATS_PERIODS } from './stats';

export const REVENUE_ATTENTION = [
  'payment_failed',
  'refunded',
  'not_scheduled',
  'setup_unfinished',
  'collecting_long',
] as const;

export const REVENUE_PAYMENT_STATUSES = [
  'open',
  'pending',
  'authorized',
  'paid',
  'failed',
  'canceled',
  'expired',
  'reversed',
] as const;

export const revenueQuerySchema = z.object({
  days: z.coerce.number().pipe(z.literal(STATS_PERIODS)).default(30),
});

const amountSchema = z.object({ cents: z.int(), count: z.int().min(0) });

export const adminRevenueSchema = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
  days: z.int(),
  received: z.object({
    current: amountSchema,
    previous: amountSchema,
    refunded: amountSchema,
    daily: z.array(z.object({ day: z.iso.date(), cents: z.int() })),
    byShop: z.array(
      z.object({
        shopId: z.uuid().nullable(),
        name: z.string(),
        cents: z.int(),
        count: z.int().min(0),
      }),
    ),
  }),
  recurring: z.object({
    monthlyCents: z.int(),
    payingFamilies: z.int().min(0),
    awaitingFamilies: z.int().min(0),
    awaitingMonthlyCents: z.int(),
    trialMonthlyCents: z.int(),
    trialFamilies: z.int().min(0),
    byInterval: z.array(
      z.object({
        interval: z.enum(BILLING_INTERVALS),
        families: z.int().min(0),
        monthlyCents: z.int(),
      }),
    ),
  }),
  collecting: amountSchema,
  paymentSync: z.object({
    checkedAt: timestampSchema.nullable(),
    stale: z.boolean(),
    failedCustomers: z.int().min(0),
  }),
  upcoming: z.object({
    week: amountSchema,
    month: amountSchema,
    firstCharges: amountSchema,
    renewals: amountSchema,
    daily: z.array(z.object({ day: z.iso.date(), cents: z.int(), count: z.int().min(0) })),
  }),
  trials: z.object({
    active: z.int().min(0),
    endingThisWeek: z.int().min(0),
    ended: z.int().min(0),
    converted: z.int().min(0),
    cancelled: z.int().min(0),
    failed: z.int().min(0),
    waiting: z.int().min(0),
  }),
  attention: z.array(
    z.object({
      kind: z.enum(REVENUE_ATTENTION),
      subscriptionId: z.uuid(),
      userId: z.uuid(),
      email: z.string(),
      orderId: z.string(),
      shopName: z.string().nullable(),
      amountCents: z.int(),
      currency: z.string(),
      since: timestampSchema,
      until: timestampSchema.nullable(),
      detail: z.string().nullable(),
    }),
  ),
});

export const revenuePaymentsQuerySchema = z.object({
  days: z.coerce.number().pipe(z.literal(STATS_PERIODS)).default(30),
  status: z.enum(REVENUE_PAYMENT_STATUSES).optional(),
  shop: z.uuid().optional(),
  ...pageQuery,
});

export const revenuePaymentListSchema = z.object({
  items: z.array(
    z.object({
      id: z.uuid(),
      at: timestampSchema,
      amountCents: z.int(),
      currency: z.string(),
      status: z.enum(REVENUE_PAYMENT_STATUSES),
      purpose: z.enum(['initial', 'renewal', 'repair', 'checkout']),
      recorded: z.boolean(),
      dashboardUrl: z.url().nullable(),
      firstCharge: z.boolean(),
      providerPaymentId: z.string(),
      orderId: z.string(),
      shopName: z.string().nullable(),
      userId: z.uuid().nullable(),
      email: z.string(),
    }),
  ),
  ...pageFields,
});

export type RevenueAttention = (typeof REVENUE_ATTENTION)[number];
export type RevenuePaymentStatus = (typeof REVENUE_PAYMENT_STATUSES)[number];
export type AdminRevenue = z.infer<typeof adminRevenueSchema>;
export type RevenuePaymentsFilters = z.output<typeof revenuePaymentsQuerySchema>;
export type RevenuePaymentsQuery = Partial<RevenuePaymentsFilters>;
export type RevenuePaymentList = z.infer<typeof revenuePaymentListSchema>;
